import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Snackbar,
  Paper,
  TablePagination
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon 
} from '@mui/icons-material';
import { 
  getCancellationPolicies, 
  getCancellationPolicy, 
  createCancellationPolicy, 
  updateCancellationPolicy, 
  deleteCancellationPolicy 
} from '../../services/cancellationPolicyService';

interface Policy {
  cancellation_policy_id?: number;
  policy_time: string;
  hrs: number;
  charges_in_perc: number;
  created_at?: string;
  updated_at?: string;
}

const CancellationPoliciesTable: React.FC = () => {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [open, setOpen] = useState(false);
  const [currentPolicy, setCurrentPolicy] = useState<Partial<Policy> | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
  try {
    const data = await getCancellationPolicies();
    console.log('✅ API response data:', data); // 👈 Add this line
    setPolicies(data);
  } catch (error) {
    console.error('Error fetching policies:', error);
    setSnackbar({
      open: true,
      message: 'Failed to fetch cancellation policies',
      severity: 'error'
    });
  }
};


  const handleOpenDialog = (policy: Partial<Policy> | null = null) => {
    if (policy) {
      setCurrentPolicy({ ...policy });
      setIsEditing(true);
    } else {
      setCurrentPolicy({ policy_time: '', hrs: 0, charges_in_perc: 0 });
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setCurrentPolicy(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentPolicy(prev => ({
      ...prev!,
      [name]: name === 'hrs' || name === 'charges_in_perc' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async () => {
    if (!currentPolicy) return;

    try {
      if (isEditing && currentPolicy.cancellation_policy_id) {
        await updateCancellationPolicy(currentPolicy.cancellation_policy_id, currentPolicy);
        setSnackbar({
          open: true,
          message: 'Policy updated successfully',
          severity: 'success'
        });
      } else {
        await createCancellationPolicy(currentPolicy as Omit<Policy, 'cancellation_policy_id'>);
        setSnackbar({
          open: true,
          message: 'Policy created successfully',
          severity: 'success'
        });
      }
      fetchPolicies();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving policy:', error);
      setSnackbar({
        open: true,
        message: 'Error saving policy',
        severity: 'error'
      });
    }
  };

  const handleDelete = async (id?: number) => {
    if (id === undefined) {
      console.error('Cannot delete policy: ID is undefined');
      setSnackbar({
        open: true,
        message: 'Error: Cannot delete policy - invalid ID',
        severity: 'error'
      });
      return;
    }

    if (window.confirm('Are you sure you want to delete this policy?')) {
      try {
        await deleteCancellationPolicy(id);
        setPolicies(policies.filter(policy => policy.cancellation_policy_id !== id));
        setSnackbar({
          open: true,
          message: 'Policy deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting policy:', error);
        setSnackbar({
          open: true,
          message: 'Error deleting policy',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, m:0 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Policy
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1, m:0, p:0 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Policy Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Hours Before</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Cancellation Charge (%)</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {policies
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((policy) => (
                <TableRow key={policy.cancellation_policy_id}>
                  <TableCell>{policy.policy_time}</TableCell>
                  <TableCell>{policy.hrs} hours</TableCell>
                  <TableCell>{policy.charges_in_perc}%</TableCell>
                  <TableCell>
                    <IconButton 
                      onClick={() => handleOpenDialog(policy)} 
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDelete(policy.cancellation_policy_id)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={policies.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit' : 'Add New'} Cancellation Policy</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Policy Description"
                  name="policy_time"
                  value={currentPolicy?.policy_time || ''}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  variant="outlined"
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Hours Before"
                  name="hrs"
                  type="number"
                  value={currentPolicy?.hrs || ''}
                  onChange={handleInputChange}
                  variant="outlined"
                  margin="normal"
                  required
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Cancellation Charge (%)"
                  name="charges_in_perc"
                  type="number"
                  value={currentPolicy?.charges_in_perc || ''}
                  onChange={handleInputChange}
                  variant="outlined"
                  margin="normal"
                  required
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!currentPolicy?.policy_time || currentPolicy.hrs === undefined || currentPolicy.charges_in_perc === undefined}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CancellationPoliciesTable;
